import { useState } from 'react';
import { motion } from 'motion/react';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, Loader2, Mail } from 'lucide-react';
import { useAuth } from '../../lib/auth/AuthContext';
import { config } from '../../lib/config';
import { Button } from '../../components/ui/Button';
import { Field, Input } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toaster';

export function SignInScreen() {
  const { signInEmail, signInGoogle, status } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy('email');
    try {
      await signInEmail(email, password);
      navigate('/', { replace: true });
    } catch {
      toast('Gagal masuk', { body: 'Periksa email dan kata sandi Anda.', tone: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const submitGoogle = async () => {
    setBusy('google');
    try {
      await signInGoogle();
      navigate('/', { replace: true });
    } catch {
      toast('Gagal masuk', { body: 'Login Google dibatalkan atau gagal.', tone: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-[460px] flex-col justify-center px-6 pb-10 pt-[max(env(safe-area-inset-top),3rem)]">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="mb-8 flex items-center gap-3">
          <span className="grid size-12 place-items-center rounded-none bg-accent text-[22px] font-black text-white">
            B
          </span>
          <div>
            <h1 className="display text-[26px] font-bold text-ink">Bimbingan</h1>
            <p className="text-[13px] text-muted">{config.appTagline}</p>
          </div>
        </div>

        {config.firebaseConfigured ? (
          <div className="card p-5">
            <div className="mb-5 flex items-center gap-2.5">
              <span className="grid size-10 place-items-center rounded-none bg-accent-soft text-accent-strong">
                <GraduationCap className="size-5" />
              </span>
              <div>
                <h2 className="text-[17px] font-bold text-ink">Masuk dengan akun</h2>
                <p className="text-[13px] text-muted">Gunakan akun institusi Anda.</p>
              </div>
            </div>

            <form onSubmit={submitEmail} className="space-y-3">
              <Field label="Email">
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nama@univ.ac.id"
                  autoComplete="email"
                />
              </Field>
              <Field label="Kata sandi">
                <Input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </Field>
              <Button block type="submit" disabled={busy !== null}>
                {busy === 'email' ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Mail className="size-4" />
                )}
                Masuk dengan email
              </Button>
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-hairline" />
                <span className="text-[12px] font-medium text-faint">atau</span>
                <div className="h-px flex-1 bg-hairline" />
              </div>
              <Button
                block
                variant="outline"
                type="button"
                onClick={submitGoogle}
                disabled={busy !== null}
              >
                {busy === 'google' ? <Loader2 className="size-4 animate-spin" /> : null}
                Lanjut dengan Google
              </Button>
            </form>

            {status === 'loading' && (
              <p className="mt-4 flex items-center justify-center gap-2 text-[12px] text-faint">
                <Loader2 className="size-3.5 animate-spin" /> Menghubungkan…
              </p>
            )}

            <p className="mt-5 text-center text-[13px] text-muted">
              Belum punya akun?{' '}
              <Link to="/register" className="font-semibold text-accent">
                Daftar
              </Link>
            </p>
          </div>
        ) : (
          <div className="card p-6 text-center">
            <h2 className="text-[16px] font-bold text-ink">Konfigurasi belum lengkap</h2>
            <p className="mt-1 text-[13px] leading-relaxed text-muted">
              Firebase Authentication belum dikonfigurasi pada lingkungan ini. Periksa variabel
              lingkungan VITE_FIREBASE_*.
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
